import { MAX_TABLES_PER_SESSION, hasGuestPaymentPending, hasPendingRound, isLocked } from './tableState';
import type { WaiterTable, WaiterTableSession } from './waiterApi';

/**
 * SC-976 regroup (2026-09-16) — the ⋮ sheet's « Transférer » / « Réunir »
 * tabs, as pure functions over the `tables/` rows. The server
 * (`POST sessions/<uuid>/regroup/`, `TableSessionService.regroup_session`)
 * re-derives all of this under its locks; this module only has to predict
 * the SAME result so the picker, the cap and the confirmation agree with
 * what will happen.
 *
 * - `move`: the tablée's tables become the picked set (first = anchor).
 * - `merge`: the picked tables are added, the anchor stays.
 * - A table carrying ANOTHER note brings that whole note: all its tables
 *   (people sit there) and its bill.
 */
export type RegroupMode = 'move' | 'merge';

/** Every table uuid of the note `table` belongs to, anchor first; a free
 *  table is its own group. */
export function groupUuids(table: WaiterTable): string[] {
  const uuids = table.session?.tableUuids;
  return uuids && uuids.length > 0 ? uuids : [table.uuid];
}

/** The tablée's own tables, anchor first. */
export function sourceUuids(source: WaiterTable): string[] {
  return groupUuids(source);
}

/** `null` when the table is free or belongs to the source note itself. */
export function otherSession(table: WaiterTable, sourceSessionUuid: string): WaiterTableSession | null {
  const session = table.session;
  if (!session || session.uuid === sourceSessionUuid) return null;
  return session;
}

/** Final ordered table set the server will produce (anchor first). */
export function regroupResult(
  mode: RegroupMode,
  source: WaiterTable,
  picked: string[],
  tablesByUuid: Map<string, WaiterTable>,
): string[] {
  const own = sourceUuids(source);
  const sessionUuid = source.session?.uuid ?? '';
  const result = mode === 'merge' ? [...own] : [];
  for (const uuid of picked) {
    const table = tablesByUuid.get(uuid);
    const expanded = table && otherSession(table, sessionUuid) ? [uuid, ...groupUuids(table)] : [uuid];
    for (const item of expanded) if (!result.includes(item)) result.push(item);
  }
  return result;
}

/** The other notes the pick absorbs, one entry per note, in pick order. */
export function absorbedSessions(
  source: WaiterTable,
  picked: string[],
  tablesByUuid: Map<string, WaiterTable>,
): WaiterTableSession[] {
  const sessionUuid = source.session?.uuid ?? '';
  const seen = new Map<string, WaiterTableSession>();
  for (const uuid of picked) {
    const table = tablesByUuid.get(uuid);
    const other = table ? otherSession(table, sessionUuid) : null;
    if (other && !seen.has(other.uuid)) seen.set(other.uuid, other);
  }
  return [...seen.values()];
}

/** Why an occupied table can't be picked at all — the server refuses to
 *  regroup it (`null` = pickable). Free tables and the source's own tables
 *  are always pickable. */
export type BlockReason = 'payment' | 'caisse';

export function blockReason(table: WaiterTable, source: WaiterTable): BlockReason | null {
  const sessionUuid = source.session?.uuid ?? '';
  if (!otherSession(table, sessionUuid)) return null;
  if (hasGuestPaymentPending(table) || hasGuestPaymentPending(source)) return 'payment';
  if (isLocked(table) || isLocked(source)) return 'caisse';
  return null;
}

/** Why another note can't be MERGED in — SC-1007: nor picked, since merging
 *  is the only way to take an occupied table. */
export type MergeBlockReason = 'pending' | 'buffet';

export function mergeBlockReason(table: WaiterTable, source: WaiterTable): MergeBlockReason | null {
  const sessionUuid = source.session?.uuid ?? '';
  if (!otherSession(table, sessionUuid)) return null;
  if (source.session?.mode === 'buffet' || table.session?.mode === 'buffet') return 'buffet';
  // Either side: the server keeps one order to validate per note.
  if (hasPendingRound(table) || hasPendingRound(source)) return 'pending';
  return null;
}

/** Tapping a chip toggles its whole group (a table of another note stands
 *  for that note). Returns the next pick list, or `null` when adding it
 *  would push the result over the cap. */
export function togglePick(
  mode: RegroupMode,
  source: WaiterTable,
  picked: string[],
  table: WaiterTable,
  tablesByUuid: Map<string, WaiterTable>,
): string[] | null {
  const sessionUuid = source.session?.uuid ?? '';
  const members = otherSession(table, sessionUuid) ? groupUuids(table) : [table.uuid];
  if (members.some((uuid) => picked.includes(uuid))) {
    return picked.filter((uuid) => !members.includes(uuid));
  }
  // The tapped table first: in `move` the first pick is the new anchor.
  const next = [...picked, table.uuid, ...members.filter((uuid) => uuid !== table.uuid)];
  return regroupResult(mode, source, next, tablesByUuid).length > MAX_TABLES_PER_SESSION ? null : next;
}

/** Tables of the result that the SOURCE loses (move only), for the
 *  confirmation's « libérées » line. */
export function freedUuids(mode: RegroupMode, source: WaiterTable, result: string[]): string[] {
  if (mode === 'merge') return [];
  return sourceUuids(source).filter((uuid) => !result.includes(uuid));
}

/** Whether a merged note is drawn as ONE chip (its first listed table
 *  standing for the group) rather than one chip per table. Always for another
 *  note — a tap picks the whole group anyway — and for the source's own
 *  tablée in `merge`, where it is locked. In `move` the source's own tables
 *  stay separate: keeping only some of them is the point of that tab. */
export function isGroupChip(mode: RegroupMode, table: WaiterTable, sessionUuid: string): boolean {
  const members = groupUuids(table);
  if (members.length < 2) return false;
  return otherSession(table, sessionUuid) !== null || mode === 'merge';
}

/** The rows the picker draws: a grouped note keeps only its representative
 *  (anchor first, else the first of its tables present in `tables`). */
export function pickerRows(mode: RegroupMode, tables: WaiterTable[], sessionUuid: string): WaiterTable[] {
  const present = new Set(tables.map((tb) => tb.uuid));
  return tables.filter((table) => {
    if (!isGroupChip(mode, table, sessionUuid)) return true;
    return groupUuids(table).find((uuid) => present.has(uuid)) === table.uuid;
  });
}
